/**
 * Story Data System for Crystals of Polkadot
 * Complete FF1-inspired storyline, NPC dialogues, and story events
 */

// ============================================================================
// STORY FLAGS - All progression points in the game
// ============================================================================

export enum StoryFlag {
  // Starting sequence
  GAME_START = 0,
  ARRIVED_CORNELIA = 1,
  TALKED_TO_KING = 2,

  // Temple of Fiends Arc
  HEARD_ABOUT_PRINCESS = 3,
  TEMPLE_UNLOCKED = 4,
  DEFEATED_GARLAND = 5,
  RESCUED_PRINCESS = 6,
  RECEIVED_LUTE = 7,

  // Bridge Arc
  BRIDGE_CEREMONY_SEEN = 8,
  BRIDGE_BUILT = 9,

  // Matoya's Cave
  FOUND_MATOYA = 10,
  HEARD_ABOUT_CRYSTAL_EYE = 11,

  // Pravoka
  ARRIVED_PRAVOKA = 12,
  DEFEATED_PIRATES = 13,
  GOT_SHIP = 14,

  // Elfheim
  ARRIVED_ELFHEIM = 15,
  LEARNED_ABOUT_ASTOS = 16,
  LEARNED_ABOUT_MARSH_CAVE = 17,

  // Marsh Cave
  ENTERED_MARSH_CAVE = 18,
  FOUND_CROWN = 19,

  // Western Keep (Astos)
  ARRIVED_WESTERN_KEEP = 20,
  DEFEATED_ASTOS = 21,
  GOT_CRYSTAL_EYE = 22,
  RETURNED_CRYSTAL_EYE = 23,
  GOT_JOLT_TONIC = 24,

  // Northwest Castle (Sleeping Prince)
  FOUND_SLEEPING_PRINCE = 25,
  WOKE_PRINCE = 26,
  LEARNED_ABOUT_VAMPIRE = 27,
  GOT_MYSTIC_KEY = 28,

  // Earth Cave
  ARRIVED_EARTH_CAVE = 29,
  GOT_STAR_RUBY = 30,
  DEFEATED_VAMPIRE = 31,

  // Titan's Tunnel
  GAVE_STAR_RUBY = 32,
  TITAN_MOVED = 33,

  // Sarda's Cave
  FOUND_SARDA = 34,
  GOT_ROD = 35,

  // Earth Crystal
  USED_ROD_ON_PLATE = 36,
  ENTERED_EARTH_CAVE_DEPTHS = 37,
  DEFEATED_LICH = 38,
  RESTORED_EARTH_CRYSTAL = 39,

  // Melmond Aftermath
  EARTH_RESTORED_CELEBRATION = 40,

  // Crescent Lake
  ARRIVED_CRESCENT_LAKE = 41,
  MET_CIRCLE_OF_SAGES = 42,
  LEARNED_ABOUT_FIENDS = 43,
  GOT_CANOE = 44,

  // Ice Cave
  ARRIVED_ICE_CAVE = 45,
  GOT_LEVISTONE = 46,

  // Airship
  USED_LEVISTONE = 47,
  GOT_AIRSHIP = 48,

  // Gaia
  ARRIVED_GAIA = 49,
  LEARNED_ABOUT_FAIRY = 50,

  // Citadel of Trials
  ENTERED_CITADEL = 51,
  COMPLETED_CITADEL = 52,
  GOT_RAT_TAIL = 53,

  // Dragon King Bahamut
  FOUND_BAHAMUT = 54,
  CLASS_UPGRADE = 55,

  // Fire Crystal
  ARRIVED_GURGU_VOLCANO = 56,
  DEFEATED_KARY = 57,
  RESTORED_FIRE_CRYSTAL = 58,

  // Water Crystal
  ARRIVED_SUNKEN_SHRINE = 59,
  GOT_SLAB = 60,
  OPENED_WATERFALL = 61,
  ARRIVED_WATERFALL_CAVERN = 62,
  GOT_CUBE = 63,
  USED_CUBE_ON_MIRAGE_TOWER = 64,
  ENTERED_SKY_CASTLE = 65,
  DEFEATED_KRAKEN = 66,
  RESTORED_WATER_CRYSTAL = 67,

  // Wind Crystal
  ARRIVED_MIRAGE_TOWER = 68,
  GOT_CHIME = 69,
  USED_CHIME = 70,
  ENTERED_FLYING_FORTRESS = 71,
  DEFEATED_TIAMAT = 72,
  RESTORED_WIND_CRYSTAL = 73,

  // Four Crystals Complete
  ALL_CRYSTALS_RESTORED = 74,

  // Temple of Fiends Revisited
  RETURNED_TO_TEMPLE = 75,
  ENTERED_TIME_VORTEX = 76,

  // Chaos Shrine
  LEARNED_TIME_LOOP_TRUTH = 77,
  DEFEATED_LICH_PAST = 78,
  DEFEATED_KARY_PAST = 79,
  DEFEATED_KRAKEN_PAST = 80,
  DEFEATED_TIAMAT_PAST = 81,

  // Final Battle
  FACED_CHAOS = 82,
  DEFEATED_CHAOS = 83,
  RESTORED_HARMONY = 84,

  // Ending
  SAW_ENDING = 85,
  GAME_COMPLETE = 86,

  // Optional Events
  FOUND_SECRET_SHOP = 90,
  DEFEATED_WARMECH = 91,
  COLLECTED_ALL_ITEMS = 92,
  MAXED_ALL_CHARACTERS = 93,
}

// ============================================================================
// LOCATION IDs - For NPC dialogue organization
// ============================================================================

export enum Location {
  // Cornelia Region
  CORNELIA = 'CORNELIA',
  CORNELIA_CASTLE = 'CORNELIA_CASTLE',
  TEMPLE_OF_FIENDS = 'TEMPLE_OF_FIENDS',
  MATOYA_CAVE = 'MATOYA_CAVE',

  // Western Continent
  PRAVOKA = 'PRAVOKA',
  ELFHEIM = 'ELFHEIM',
  WESTERN_KEEP = 'WESTERN_KEEP',
  NORTHWEST_CASTLE = 'NORTHWEST_CASTLE',
  MARSH_CAVE = 'MARSH_CAVE',

  // Northern Continent
  MELMOND = 'MELMOND',
  EARTH_CAVE = 'EARTH_CAVE',
  TITANS_TUNNEL = 'TITANS_TUNNEL',
  SARDA_CAVE = 'SARDA_CAVE',

  // Crescent Lake Region
  CRESCENT_LAKE = 'CRESCENT_LAKE',
  ICE_CAVE = 'ICE_CAVE',

  // Eastern Continent
  GAIA = 'GAIA',
  ONRAC = 'ONRAC',
  WATERFALL_CAVERN = 'WATERFALL_CAVERN',
  SUNKEN_SHRINE = 'SUNKEN_SHRINE',

  // Late Game
  CITADEL_OF_TRIALS = 'CITADEL_OF_TRIALS',
  BAHAMUT_CAVE = 'BAHAMUT_CAVE',
  GURGU_VOLCANO = 'GURGU_VOLCANO',
  MIRAGE_TOWER = 'MIRAGE_TOWER',
  SKY_CASTLE = 'SKY_CASTLE',
  FLYING_FORTRESS = 'FLYING_FORTRESS',

  // Chaos Shrine
  CHAOS_SHRINE = 'CHAOS_SHRINE',
}

// ============================================================================
// DIALOGUE SYSTEM TYPES
// ============================================================================

export interface DialogueLine {
  speaker: string;
  text: string;
  portrait?: string; // Optional character portrait
  options?: DialogueOption[]; // For branching dialogue
  nextId?: string; // Next dialogue in sequence
  sfx?: string; // Sound effect to play
}

export interface DialogueOption {
  text: string;
  nextId: string;
  condition?: StoryFlag; // Only show if flag is set
}

export interface DialogueTree {
  id: string;
  entryPoint: string; // First dialogue ID
  dialogues: Map<string, DialogueLine>;
}

export interface NPCDialogue {
  npcId: string;
  name: string;
  location: Location;
  dialogues: ConditionalDialogue[];
}

export interface ConditionalDialogue {
  conditions: StoryCondition[];
  text: string;
  multiline?: boolean; // For longer speeches
}

export interface StoryCondition {
  flag?: StoryFlag;
  flagSet?: boolean; // True = must be set, false = must NOT be set
  operator?: 'AND' | 'OR';
}

export interface StoryEvent {
  id: string;
  name: string;
  location: Location;
  trigger: EventTrigger;
  conditions: StoryCondition[];
  script: DialogueLine[];
  rewards?: EventReward[];
  flagsToSet: StoryFlag[];
}

export interface EventTrigger {
  type: 'ENTER_MAP' | 'TALK_TO_NPC' | 'DEFEAT_BOSS' | 'USE_ITEM' | 'POSITION';
  target?: string; // NPC ID, Boss ID, Item ID, or coordinates
}

export interface EventReward {
  type: 'ITEM' | 'GIL' | 'EXPERIENCE' | 'KEY_ITEM';
  id?: number;
  amount?: number;
}

// ============================================================================
// MAIN STORY SCRIPT
// ============================================================================

export const MAIN_STORYLINE = {
  title: "Crystals of Polkadot",
  subtitle: "A Tale of Light and Darkness",

  prologue: [
    "The world is veiled in darkness.",
    "The wind stops, the sea rages,",
    "and the earth begins to rot.",
    "",
    "The people wait, their only hope,",
    "a prophecy...",
    "",
    "'When darkness veils the world,",
    "four Warriors of Light shall come.'",
    "",
    "After a long journey,",
    "four young warriors arrive",
    "bearing the crystals of light...",
  ],

  acts: {
    act1: {
      title: "The Princess and the Knight",
      summary: "Rescue Princess Sara from the fallen knight Garland",
      keyEvents: [
        StoryFlag.ARRIVED_CORNELIA,
        StoryFlag.DEFEATED_GARLAND,
        StoryFlag.RESCUED_PRINCESS,
        StoryFlag.BRIDGE_BUILT,
      ],
    },

    act2: {
      title: "The Crystal of Earth",
      summary: "Journey to Melmond and restore the Earth Crystal",
      keyEvents: [
        StoryFlag.GOT_SHIP,
        StoryFlag.DEFEATED_VAMPIRE,
        StoryFlag.DEFEATED_LICH,
        StoryFlag.RESTORED_EARTH_CRYSTAL,
      ],
    },

    act3: {
      title: "The Sages and the Airship",
      summary: "Meet the Circle of Sages and obtain the Airship",
      keyEvents: [
        StoryFlag.MET_CIRCLE_OF_SAGES,
        StoryFlag.GOT_CANOE,
        StoryFlag.GOT_AIRSHIP,
        StoryFlag.CLASS_UPGRADE,
      ],
    },

    act4: {
      title: "The Four Fiends",
      summary: "Defeat the four elemental Fiends and restore all Crystals",
      keyEvents: [
        StoryFlag.DEFEATED_KARY,
        StoryFlag.DEFEATED_KRAKEN,
        StoryFlag.DEFEATED_TIAMAT,
        StoryFlag.ALL_CRYSTALS_RESTORED,
      ],
    },

    act5: {
      title: "The Time Loop",
      summary: "Discover the truth and face Chaos in the past",
      keyEvents: [
        StoryFlag.ENTERED_TIME_VORTEX,
        StoryFlag.LEARNED_TIME_LOOP_TRUTH,
        StoryFlag.DEFEATED_CHAOS,
      ],
    },
  },

  epilogue: [
    "And so, their journey ends.",
    "The Warriors of Light",
    "defeated Chaos and broke",
    "the endless cycle.",
    "",
    "The Crystals glow once more,",
    "and peace returns to the world.",
    "",
    "But time itself was altered...",
    "Will anyone remember their deeds?",
    "",
    "The four Warriors walked",
    "into the light, their quest complete,",
    "their names lost to legend...",
  ],
};

// ============================================================================
// KEY STORY EVENTS
// ============================================================================

export const STORY_EVENTS: StoryEvent[] = [
  // -------------------------------------------------------------------------
  // Opening Sequence
  // -------------------------------------------------------------------------
  {
    id: 'OPENING',
    name: 'Game Opening',
    location: Location.CORNELIA,
    trigger: { type: 'ENTER_MAP' },
    conditions: [{ flag: StoryFlag.GAME_START, flagSet: false }],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'The world is veiled in darkness...',
      },
      {
        speaker: 'NARRATOR',
        text: 'Four Warriors of Light arrive in Cornelia, bearing ancient crystals.',
      },
    ],
    flagsToSet: [StoryFlag.GAME_START, StoryFlag.ARRIVED_CORNELIA],
  },

  // -------------------------------------------------------------------------
  // King of Cornelia
  // -------------------------------------------------------------------------
  {
    id: 'MEET_KING',
    name: 'Audience with the King',
    location: Location.CORNELIA_CASTLE,
    trigger: { type: 'TALK_TO_NPC', target: 'KING' },
    conditions: [{ flag: StoryFlag.TALKED_TO_KING, flagSet: false }],
    script: [
      {
        speaker: 'King of Cornelia',
        text: 'Welcome, travelers. I am the King of Cornelia.',
      },
      {
        speaker: 'King of Cornelia',
        text: 'You bear the Crystals... Could you be the Warriors of Light from the prophecy?',
      },
      {
        speaker: 'King of Cornelia',
        text: 'Please, you must help us! My daughter, Princess Sara, has been kidnapped!',
      },
      {
        speaker: 'King of Cornelia',
        text: 'A knight named Garland has taken her to the Temple of Fiends to the north.',
      },
      {
        speaker: 'King of Cornelia',
        text: 'I beg you, Warriors of Light... Save my daughter!',
      },
    ],
    flagsToSet: [StoryFlag.TALKED_TO_KING, StoryFlag.HEARD_ABOUT_PRINCESS, StoryFlag.TEMPLE_UNLOCKED],
  },

  // -------------------------------------------------------------------------
  // Defeat Garland
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_GARLAND',
    name: 'Battle with Garland',
    location: Location.TEMPLE_OF_FIENDS,
    trigger: { type: 'DEFEAT_BOSS', target: 'GARLAND' },
    conditions: [{ flag: StoryFlag.DEFEATED_GARLAND, flagSet: false }],
    script: [
      {
        speaker: 'Garland',
        text: 'Impossible! I, Garland, will knock you all down!',
      },
      {
        speaker: 'Garland',
        text: '...No... How could this be?',
      },
      {
        speaker: 'Garland',
        text: 'This is not over... I will return... Two thousand years in the past...',
      },
      {
        speaker: 'Princess Sara',
        text: 'Thank you, brave warriors! You saved me!',
      },
    ],
    flagsToSet: [StoryFlag.DEFEATED_GARLAND, StoryFlag.RESCUED_PRINCESS],
  },

  // -------------------------------------------------------------------------
  // Return Princess to Castle
  // -------------------------------------------------------------------------
  {
    id: 'RETURN_PRINCESS',
    name: 'Princess Returns',
    location: Location.CORNELIA_CASTLE,
    trigger: { type: 'ENTER_MAP' },
    conditions: [
      { flag: StoryFlag.RESCUED_PRINCESS, flagSet: true },
      { flag: StoryFlag.RECEIVED_LUTE, flagSet: false },
    ],
    script: [
      {
        speaker: 'King of Cornelia',
        text: 'Sara! Oh, thank the crystals! You\'re safe!',
      },
      {
        speaker: 'Princess Sara',
        text: 'Father! These warriors saved me from Garland.',
      },
      {
        speaker: 'King of Cornelia',
        text: 'Warriors of Light, you have my eternal gratitude!',
      },
      {
        speaker: 'King of Cornelia',
        text: 'As a token of my thanks, I shall have the bridge north of Cornelia repaired.',
      },
      {
        speaker: 'Princess Sara',
        text: 'Please, take this LUTE. It has been in our family for generations.',
      },
      {
        speaker: 'Princess Sara',
        text: 'It is said to hold mystical power. May it aid you on your journey.',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 1 }], // Lute
    flagsToSet: [StoryFlag.RECEIVED_LUTE],
  },

  // -------------------------------------------------------------------------
  // Bridge Ceremony
  // -------------------------------------------------------------------------
  {
    id: 'BRIDGE_CEREMONY',
    name: 'The Bridge is Built',
    location: Location.CORNELIA,
    trigger: { type: 'ENTER_MAP' },
    conditions: [
      { flag: StoryFlag.RECEIVED_LUTE, flagSet: true },
      { flag: StoryFlag.BRIDGE_BUILT, flagSet: false },
    ],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'The people of Cornelia work tirelessly to rebuild the bridge...',
      },
      {
        speaker: 'NARRATOR',
        text: 'After some time, the bridge is complete!',
      },
      {
        speaker: 'Minister',
        text: 'Thanks to the Warriors of Light, we can now cross to the northern lands!',
      },
    ],
    flagsToSet: [StoryFlag.BRIDGE_CEREMONY_SEEN, StoryFlag.BRIDGE_BUILT],
  },

  // -------------------------------------------------------------------------
  // Matoya's Cave
  // -------------------------------------------------------------------------
  {
    id: 'MEET_MATOYA',
    name: 'The Blind Witch',
    location: Location.MATOYA_CAVE,
    trigger: { type: 'TALK_TO_NPC', target: 'MATOYA' },
    conditions: [{ flag: StoryFlag.FOUND_MATOYA, flagSet: false }],
    script: [
      {
        speaker: 'Matoya',
        text: 'Who goes there? I can\'t see you...',
      },
      {
        speaker: 'Matoya',
        text: 'My CRYSTAL EYE was stolen by a dark elf named Astos.',
      },
      {
        speaker: 'Matoya',
        text: 'If you find it and return it to me, I shall reward you.',
      },
    ],
    flagsToSet: [StoryFlag.FOUND_MATOYA, StoryFlag.HEARD_ABOUT_CRYSTAL_EYE],
  },

  // -------------------------------------------------------------------------
  // Defeat Pirates, Get Ship
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_PIRATES',
    name: 'Pirates of Pravoka',
    location: Location.PRAVOKA,
    trigger: { type: 'DEFEAT_BOSS', target: 'BIKKE' },
    conditions: [{ flag: StoryFlag.DEFEATED_PIRATES, flagSet: false }],
    script: [
      {
        speaker: 'Bikke',
        text: 'Arg! Ye scurvy dogs! Ye beat me!',
      },
      {
        speaker: 'Bikke',
        text: 'Fine! Take me ship! But ye haven\'t seen the last of Bikke!',
      },
      {
        speaker: 'NARRATOR',
        text: 'You obtained the SHIP!',
      },
    ],
    flagsToSet: [StoryFlag.DEFEATED_PIRATES, StoryFlag.GOT_SHIP],
  },

  // -------------------------------------------------------------------------
  // Astos Boss Fight
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_ASTOS',
    name: 'The Dark Elf King',
    location: Location.WESTERN_KEEP,
    trigger: { type: 'DEFEAT_BOSS', target: 'ASTOS' },
    conditions: [{ flag: StoryFlag.DEFEATED_ASTOS, flagSet: false }],
    script: [
      {
        speaker: 'Astos',
        text: 'Fools! I am not the prince!',
      },
      {
        speaker: 'Astos',
        text: 'I am Astos, King of the Dark Elves! And you shall die!',
      },
      {
        speaker: 'NARRATOR',
        text: '...',
      },
      {
        speaker: 'Astos',
        text: 'Impossible... Defeated by mere humans...',
      },
      {
        speaker: 'NARRATOR',
        text: 'You obtained the CRYSTAL EYE!',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 2 }], // Crystal Eye
    flagsToSet: [StoryFlag.DEFEATED_ASTOS, StoryFlag.GOT_CRYSTAL_EYE],
  },

  // -------------------------------------------------------------------------
  // Return Crystal Eye to Matoya
  // -------------------------------------------------------------------------
  {
    id: 'RETURN_CRYSTAL_EYE',
    name: 'Restore Matoya\'s Sight',
    location: Location.MATOYA_CAVE,
    trigger: { type: 'USE_ITEM', target: 'CRYSTAL_EYE' },
    conditions: [
      { flag: StoryFlag.GOT_CRYSTAL_EYE, flagSet: true },
      { flag: StoryFlag.RETURNED_CRYSTAL_EYE, flagSet: false },
    ],
    script: [
      {
        speaker: 'Matoya',
        text: 'My CRYSTAL EYE! You found it!',
      },
      {
        speaker: 'Matoya',
        text: 'Ah, I can see again! Thank you, warriors!',
      },
      {
        speaker: 'Matoya',
        text: 'Take this JOLT TONIC. It can wake someone from an eternal sleep.',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 3 }], // Jolt Tonic
    flagsToSet: [StoryFlag.RETURNED_CRYSTAL_EYE, StoryFlag.GOT_JOLT_TONIC],
  },

  // -------------------------------------------------------------------------
  // Wake the Sleeping Prince
  // -------------------------------------------------------------------------
  {
    id: 'WAKE_PRINCE',
    name: 'The Cursed Prince',
    location: Location.NORTHWEST_CASTLE,
    trigger: { type: 'USE_ITEM', target: 'JOLT_TONIC' },
    conditions: [
      { flag: StoryFlag.GOT_JOLT_TONIC, flagSet: true },
      { flag: StoryFlag.WOKE_PRINCE, flagSet: false },
    ],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'You use the JOLT TONIC on the sleeping prince...',
      },
      {
        speaker: 'Prince',
        text: '*cough* Wh-where am I? How long have I been asleep?',
      },
      {
        speaker: 'Prince',
        text: 'A vampire in the Earth Cave put this curse upon me!',
      },
      {
        speaker: 'Prince',
        text: 'Thank you for breaking it! Please, take this MYSTIC KEY.',
      },
      {
        speaker: 'Prince',
        text: 'It can open any locked door in the world.',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 4 }], // Mystic Key
    flagsToSet: [StoryFlag.WOKE_PRINCE, StoryFlag.LEARNED_ABOUT_VAMPIRE, StoryFlag.GOT_MYSTIC_KEY],
  },

  // -------------------------------------------------------------------------
  // Defeat Vampire
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_VAMPIRE',
    name: 'The Vampire Lord',
    location: Location.EARTH_CAVE,
    trigger: { type: 'DEFEAT_BOSS', target: 'VAMPIRE' },
    conditions: [{ flag: StoryFlag.DEFEATED_VAMPIRE, flagSet: false }],
    script: [
      {
        speaker: 'Vampire',
        text: 'The light... it burns! Curse you, Warriors of Light!',
      },
      {
        speaker: 'NARRATOR',
        text: 'You obtained the STAR RUBY!',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 5 }], // Star Ruby
    flagsToSet: [StoryFlag.DEFEATED_VAMPIRE, StoryFlag.GOT_STAR_RUBY],
  },

  // -------------------------------------------------------------------------
  // Titan Moves
  // -------------------------------------------------------------------------
  {
    id: 'TITAN_MOVES',
    name: 'The Grateful Titan',
    location: Location.TITANS_TUNNEL,
    trigger: { type: 'USE_ITEM', target: 'STAR_RUBY' },
    conditions: [
      { flag: StoryFlag.GOT_STAR_RUBY, flagSet: true },
      { flag: StoryFlag.TITAN_MOVED, flagSet: false },
    ],
    script: [
      {
        speaker: 'Titan',
        text: 'STAR RUBY! You brought STAR RUBY!',
      },
      {
        speaker: 'Titan',
        text: 'THANK YOU! TITAN MOVE NOW!',
      },
      {
        speaker: 'NARRATOR',
        text: 'The Titan moves aside, revealing a passage!',
      },
    ],
    flagsToSet: [StoryFlag.GAVE_STAR_RUBY, StoryFlag.TITAN_MOVED],
  },

  // -------------------------------------------------------------------------
  // Sarda Gives Rod
  // -------------------------------------------------------------------------
  {
    id: 'SARDA_GIVES_ROD',
    name: 'The Sage Sarda',
    location: Location.SARDA_CAVE,
    trigger: { type: 'TALK_TO_NPC', target: 'SARDA' },
    conditions: [
      { flag: StoryFlag.TITAN_MOVED, flagSet: true },
      { flag: StoryFlag.GOT_ROD, flagSet: false },
    ],
    script: [
      {
        speaker: 'Sarda',
        text: 'So, you passed through the Titan\'s Tunnel. Impressive.',
      },
      {
        speaker: 'Sarda',
        text: 'I am Sarda, the sage. I foresaw your coming.',
      },
      {
        speaker: 'Sarda',
        text: 'Take this EARTH ROD. It will open the way to the Earth Fiend.',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 6 }], // Earth Rod
    flagsToSet: [StoryFlag.FOUND_SARDA, StoryFlag.GOT_ROD],
  },

  // -------------------------------------------------------------------------
  // Defeat Lich (Earth Fiend)
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_LICH',
    name: 'The Earth Fiend',
    location: Location.EARTH_CAVE,
    trigger: { type: 'DEFEAT_BOSS', target: 'LICH' },
    conditions: [{ flag: StoryFlag.DEFEATED_LICH, flagSet: false }],
    script: [
      {
        speaker: 'Lich',
        text: 'Two thousand years... Waiting... For this moment...',
      },
      {
        speaker: 'Lich',
        text: 'But you are too late! The time loop... has already begun!',
      },
      {
        speaker: 'Lich',
        text: 'Nooo...!',
      },
      {
        speaker: 'NARRATOR',
        text: 'The EARTH CRYSTAL begins to glow!',
      },
      {
        speaker: 'NARRATOR',
        text: 'Its light is restored!',
      },
    ],
    flagsToSet: [StoryFlag.DEFEATED_LICH, StoryFlag.RESTORED_EARTH_CRYSTAL],
  },

  // -------------------------------------------------------------------------
  // Circle of Sages
  // -------------------------------------------------------------------------
  {
    id: 'CIRCLE_OF_SAGES',
    name: 'The Prophecy Unfolds',
    location: Location.CRESCENT_LAKE,
    trigger: { type: 'ENTER_MAP' },
    conditions: [
      { flag: StoryFlag.ARRIVED_CRESCENT_LAKE, flagSet: false },
    ],
    script: [
      {
        speaker: 'Elder Sage',
        text: 'Welcome, Warriors of Light. We are the Circle of Sages.',
      },
      {
        speaker: 'Elder Sage',
        text: 'We have been expecting you. The prophecy spoke of your arrival.',
      },
      {
        speaker: 'Second Sage',
        text: 'Four elemental Fiends have corrupted the Crystals of Light.',
      },
      {
        speaker: 'Third Sage',
        text: 'Lich of Earth, Kary of Fire, Kraken of Water, and Tiamat of Wind.',
      },
      {
        speaker: 'Fourth Sage',
        text: 'You must defeat all four and restore the Crystals.',
      },
      {
        speaker: 'Elder Sage',
        text: 'Take this CANOE. It will help you in your journey.',
      },
    ],
    rewards: [{ type: 'KEY_ITEM', id: 7 }], // Canoe
    flagsToSet: [
      StoryFlag.ARRIVED_CRESCENT_LAKE,
      StoryFlag.MET_CIRCLE_OF_SAGES,
      StoryFlag.LEARNED_ABOUT_FIENDS,
      StoryFlag.GOT_CANOE,
    ],
  },

  // -------------------------------------------------------------------------
  // Get Airship
  // -------------------------------------------------------------------------
  {
    id: 'GET_AIRSHIP',
    name: 'The Ancient Airship',
    location: Location.ONRAC,
    trigger: { type: 'USE_ITEM', target: 'LEVISTONE' },
    conditions: [
      { flag: StoryFlag.GOT_LEVISTONE, flagSet: true },
      { flag: StoryFlag.GOT_AIRSHIP, flagSet: false },
    ],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'You place the LEVISTONE in the ancient altar...',
      },
      {
        speaker: 'NARRATOR',
        text: 'The ground shakes! An airship rises from the desert!',
      },
      {
        speaker: 'NARRATOR',
        text: 'You obtained the AIRSHIP!',
      },
    ],
    flagsToSet: [StoryFlag.USED_LEVISTONE, StoryFlag.GOT_AIRSHIP],
  },

  // -------------------------------------------------------------------------
  // Class Change (Bahamut)
  // -------------------------------------------------------------------------
  {
    id: 'CLASS_CHANGE',
    name: 'Trial of Bahamut',
    location: Location.BAHAMUT_CAVE,
    trigger: { type: 'USE_ITEM', target: 'RAT_TAIL' },
    conditions: [
      { flag: StoryFlag.GOT_RAT_TAIL, flagSet: true },
      { flag: StoryFlag.CLASS_UPGRADE, flagSet: false },
    ],
    script: [
      {
        speaker: 'Bahamut',
        text: 'You bring the RAT TAIL from the Citadel of Trials?',
      },
      {
        speaker: 'Bahamut',
        text: 'Then you are worthy! I, Bahamut, King of Dragons, grant you my blessing!',
      },
      {
        speaker: 'Bahamut',
        text: 'Warriors of Light... You shall now become LEGENDS!',
      },
      {
        speaker: 'NARRATOR',
        text: 'Your party undergoes the CLASS CHANGE!',
      },
      {
        speaker: 'NARRATOR',
        text: 'FIGHTER becomes KNIGHT!',
        nextId: 'cc2',
      },
      {
        speaker: 'NARRATOR',
        text: 'THIEF becomes NINJA!',
        nextId: 'cc3',
      },
      {
        speaker: 'NARRATOR',
        text: 'MONK becomes MASTER!',
        nextId: 'cc4',
      },
      {
        speaker: 'NARRATOR',
        text: 'RED MAGE becomes RED WIZARD!',
        nextId: 'cc5',
      },
      {
        speaker: 'NARRATOR',
        text: 'WHITE MAGE becomes WHITE WIZARD!',
        nextId: 'cc6',
      },
      {
        speaker: 'NARRATOR',
        text: 'BLACK MAGE becomes BLACK WIZARD!',
      },
    ],
    flagsToSet: [StoryFlag.FOUND_BAHAMUT, StoryFlag.CLASS_UPGRADE],
  },

  // -------------------------------------------------------------------------
  // Defeat Kary (Fire Fiend)
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_KARY',
    name: 'The Fire Fiend',
    location: Location.GURGU_VOLCANO,
    trigger: { type: 'DEFEAT_BOSS', target: 'KARY' },
    conditions: [{ flag: StoryFlag.DEFEATED_KARY, flagSet: false }],
    script: [
      {
        speaker: 'Kary',
        text: 'You dare challenge the flames? I am KARY, Fiend of Fire!',
      },
      {
        speaker: 'Kary',
        text: 'Burn! Burn in the fires of eternity!',
      },
      {
        speaker: 'NARRATOR',
        text: '...',
      },
      {
        speaker: 'Kary',
        text: 'Impossible... The flames... grow cold...',
      },
      {
        speaker: 'NARRATOR',
        text: 'The FIRE CRYSTAL is restored!',
      },
    ],
    flagsToSet: [StoryFlag.DEFEATED_KARY, StoryFlag.RESTORED_FIRE_CRYSTAL],
  },

  // -------------------------------------------------------------------------
  // Defeat Kraken (Water Fiend)
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_KRAKEN',
    name: 'The Water Fiend',
    location: Location.SUNKEN_SHRINE,
    trigger: { type: 'DEFEAT_BOSS', target: 'KRAKEN' },
    conditions: [{ flag: StoryFlag.DEFEATED_KRAKEN, flagSet: false }],
    script: [
      {
        speaker: 'Kraken',
        text: 'From the depths I rise! I am KRAKEN, Fiend of Water!',
      },
      {
        speaker: 'Kraken',
        text: 'You shall drown in an endless abyss!',
      },
      {
        speaker: 'NARRATOR',
        text: '...',
      },
      {
        speaker: 'Kraken',
        text: 'The tide... has turned... against me...',
      },
      {
        speaker: 'NARRATOR',
        text: 'The WATER CRYSTAL is restored!',
      },
    ],
    flagsToSet: [StoryFlag.DEFEATED_KRAKEN, StoryFlag.RESTORED_WATER_CRYSTAL],
  },

  // -------------------------------------------------------------------------
  // Defeat Tiamat (Wind Fiend)
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_TIAMAT',
    name: 'The Wind Fiend',
    location: Location.FLYING_FORTRESS,
    trigger: { type: 'DEFEAT_BOSS', target: 'TIAMAT' },
    conditions: [{ flag: StoryFlag.DEFEATED_TIAMAT, flagSet: false }],
    script: [
      {
        speaker: 'Tiamat',
        text: 'You have climbed high, mortals. I am TIAMAT, Fiend of Wind!',
      },
      {
        speaker: 'Tiamat',
        text: 'The storms shall tear you apart!',
      },
      {
        speaker: 'NARRATOR',
        text: '...',
      },
      {
        speaker: 'Tiamat',
        text: 'The winds... they no longer obey me...',
      },
      {
        speaker: 'NARRATOR',
        text: 'The WIND CRYSTAL is restored!',
      },
      {
        speaker: 'NARRATOR',
        text: 'All four Crystals now shine with pure light!',
      },
    ],
    flagsToSet: [
      StoryFlag.DEFEATED_TIAMAT,
      StoryFlag.RESTORED_WIND_CRYSTAL,
      StoryFlag.ALL_CRYSTALS_RESTORED,
    ],
  },

  // -------------------------------------------------------------------------
  // Return to Temple of Fiends
  // -------------------------------------------------------------------------
  {
    id: 'RETURN_TO_TEMPLE',
    name: 'The Time Vortex Opens',
    location: Location.TEMPLE_OF_FIENDS,
    trigger: { type: 'ENTER_MAP' },
    conditions: [
      { flag: StoryFlag.ALL_CRYSTALS_RESTORED, flagSet: true },
      { flag: StoryFlag.ENTERED_TIME_VORTEX, flagSet: false },
    ],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'The four Crystals begin to resonate...',
      },
      {
        speaker: 'NARRATOR',
        text: 'A vortex of swirling darkness appears in the Temple of Fiends!',
      },
      {
        speaker: 'Voice',
        text: '...Come... Come to the past... Come to the beginning...',
      },
      {
        speaker: 'NARRATOR',
        text: 'You step into the vortex...',
      },
    ],
    flagsToSet: [StoryFlag.RETURNED_TO_TEMPLE, StoryFlag.ENTERED_TIME_VORTEX],
  },

  // -------------------------------------------------------------------------
  // Time Loop Revelation
  // -------------------------------------------------------------------------
  {
    id: 'TIME_LOOP_REVELATION',
    name: 'The Truth of Chaos',
    location: Location.CHAOS_SHRINE,
    trigger: { type: 'ENTER_MAP' },
    conditions: [
      { flag: StoryFlag.ENTERED_TIME_VORTEX, flagSet: true },
      { flag: StoryFlag.LEARNED_TIME_LOOP_TRUTH, flagSet: false },
    ],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'You find yourself in the Temple of Fiends... but 2000 years in the past!',
      },
      {
        speaker: 'Garland',
        text: 'Heh heh heh... So you\'ve finally arrived.',
      },
      {
        speaker: 'Garland',
        text: 'You defeated me in your time. But here, I am more than Garland!',
      },
      {
        speaker: 'Garland',
        text: 'The Four Fiends sent me back in time. I sent them to the future.',
      },
      {
        speaker: 'Garland',
        text: 'An endless cycle! I gain power from their future, they from my past!',
      },
      {
        speaker: 'Garland',
        text: 'I am... I am CHAOS! The embodiment of discord!',
      },
      {
        speaker: 'Garland',
        text: 'And now, you shall DIE!',
      },
    ],
    flagsToSet: [StoryFlag.LEARNED_TIME_LOOP_TRUTH],
  },

  // -------------------------------------------------------------------------
  // Defeat Chaos (Final Boss)
  // -------------------------------------------------------------------------
  {
    id: 'DEFEAT_CHAOS',
    name: 'The Final Battle',
    location: Location.CHAOS_SHRINE,
    trigger: { type: 'DEFEAT_BOSS', target: 'CHAOS' },
    conditions: [{ flag: StoryFlag.DEFEATED_CHAOS, flagSet: false }],
    script: [
      {
        speaker: 'Chaos',
        text: 'No... NOOO! The cycle... is broken!',
      },
      {
        speaker: 'Chaos',
        text: 'Two thousand years... of power... fading...',
      },
      {
        speaker: 'Chaos',
        text: 'You have... defeated... Chaos...',
      },
      {
        speaker: 'NARRATOR',
        text: 'The darkness dissipates. The temple crumbles around you.',
      },
      {
        speaker: 'NARRATOR',
        text: 'The four Crystals shine brighter than ever before!',
      },
      {
        speaker: 'NARRATOR',
        text: 'Peace returns to the world... but time itself has been altered.',
      },
      {
        speaker: 'NARRATOR',
        text: 'Will anyone remember what happened?',
      },
    ],
    flagsToSet: [StoryFlag.DEFEATED_CHAOS, StoryFlag.RESTORED_HARMONY],
  },

  // -------------------------------------------------------------------------
  // Ending Sequence
  // -------------------------------------------------------------------------
  {
    id: 'ENDING',
    name: 'The Warriors Return',
    location: Location.CORNELIA,
    trigger: { type: 'ENTER_MAP' },
    conditions: [
      { flag: StoryFlag.DEFEATED_CHAOS, flagSet: true },
      { flag: StoryFlag.SAW_ENDING, flagSet: false },
    ],
    script: [
      {
        speaker: 'NARRATOR',
        text: 'You return to Cornelia, the place where your journey began.',
      },
      {
        speaker: 'Princess Sara',
        text: 'Oh! Brave warriors! You look weary from a long journey.',
      },
      {
        speaker: 'Princess Sara',
        text: '...Though strangely, I feel like we\'ve met before.',
      },
      {
        speaker: 'King of Cornelia',
        text: 'Your crystals shine so brightly! Surely you must be the Warriors of Light!',
      },
      {
        speaker: 'NARRATOR',
        text: 'The world has been saved, but history has been rewritten.',
      },
      {
        speaker: 'NARRATOR',
        text: 'The people no longer remember the Four Fiends or the time loop.',
      },
      {
        speaker: 'NARRATOR',
        text: 'Only you, the Warriors of Light, carry those memories.',
      },
      {
        speaker: 'NARRATOR',
        text: 'And so, your legend begins anew...',
      },
    ],
    flagsToSet: [StoryFlag.SAW_ENDING, StoryFlag.GAME_COMPLETE],
  },
];

// ============================================================================
// NPC DIALOGUES BY LOCATION
// ============================================================================

export const NPC_DIALOGUES: NPCDialogue[] = [
  // =========================================================================
  // CORNELIA - Starting Town
  // =========================================================================
  {
    npcId: 'CORNELIA_OLD_MAN',
    name: 'Old Man',
    location: Location.CORNELIA,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.GAME_START, flagSet: false }],
        text: 'Welcome to Cornelia, travelers!',
      },
      {
        conditions: [
          { flag: StoryFlag.GAME_START, flagSet: true },
          { flag: StoryFlag.DEFEATED_GARLAND, flagSet: false },
        ],
        text: 'Princess Sara was taken by a knight named Garland. Please save her!',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_GARLAND, flagSet: true }],
        text: 'You saved the Princess! You truly are the Warriors of Light!',
      },
      {
        conditions: [{ flag: StoryFlag.BRIDGE_BUILT, flagSet: true }],
        text: 'The bridge is complete! Now you can explore the northern lands!',
      },
    ],
  },

  {
    npcId: 'CORNELIA_WOMAN',
    name: 'Woman',
    location: Location.CORNELIA,
    dialogues: [
      {
        conditions: [],
        text: 'The King\'s Castle is to the north. You should speak with him.',
      },
      {
        conditions: [{ flag: StoryFlag.RESCUED_PRINCESS, flagSet: true }],
        text: 'Princess Sara is such a kind soul. I\'m so glad she\'s safe!',
      },
    ],
  },

  {
    npcId: 'CORNELIA_GUARD',
    name: 'Guard',
    location: Location.CORNELIA,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.BRIDGE_BUILT, flagSet: false }],
        text: 'The bridge to the north has been out for years. We\'re cut off from the rest of the world.',
      },
      {
        conditions: [{ flag: StoryFlag.BRIDGE_BUILT, flagSet: true }],
        text: 'The bridge is repaired! Be careful out there in the wilderness.',
      },
    ],
  },

  {
    npcId: 'CORNELIA_SHOP_KEEPER',
    name: 'Shop Keeper',
    location: Location.CORNELIA,
    dialogues: [
      {
        conditions: [],
        text: 'Welcome! We have basic supplies for your journey.',
      },
    ],
  },

  {
    npcId: 'CORNELIA_INN_KEEPER',
    name: 'Inn Keeper',
    location: Location.CORNELIA,
    dialogues: [
      {
        conditions: [],
        text: 'Rest here for 50 gil. Sweet dreams!',
      },
    ],
  },

  {
    npcId: 'CORNELIA_SCHOLAR',
    name: 'Scholar',
    location: Location.CORNELIA,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.GAME_START, flagSet: true }],
        text: 'An ancient prophecy speaks of four Warriors of Light who will save the world.',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_LICH, flagSet: true }],
        text: 'The Earth Crystal shines again! The prophecy is coming true!',
      },
      {
        conditions: [{ flag: StoryFlag.ALL_CRYSTALS_RESTORED, flagSet: true }],
        text: 'All four Crystals are restored! But I sense a great evil still lurks...',
      },
    ],
  },

  // =========================================================================
  // CORNELIA CASTLE
  // =========================================================================
  {
    npcId: 'KING',
    name: 'King of Cornelia',
    location: Location.CORNELIA_CASTLE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.TALKED_TO_KING, flagSet: false }],
        text: 'You bear the Crystals! Please, save my daughter from Garland!',
      },
      {
        conditions: [
          { flag: StoryFlag.TALKED_TO_KING, flagSet: true },
          { flag: StoryFlag.DEFEATED_GARLAND, flagSet: false },
        ],
        text: 'Garland took Sara to the Temple of Fiends. Please hurry!',
      },
      {
        conditions: [{ flag: StoryFlag.RESCUED_PRINCESS, flagSet: true }],
        text: 'Thank you for saving Sara! Take the LUTE as a token of our gratitude.',
      },
      {
        conditions: [{ flag: StoryFlag.BRIDGE_BUILT, flagSet: true }],
        text: 'May your journey bring light to the world, brave warriors!',
      },
    ],
  },

  {
    npcId: 'PRINCESS_SARA',
    name: 'Princess Sara',
    location: Location.CORNELIA_CASTLE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.RESCUED_PRINCESS, flagSet: false }],
        text: '(She is not here - kidnapped by Garland)',
      },
      {
        conditions: [{ flag: StoryFlag.RESCUED_PRINCESS, flagSet: true }],
        text: 'Thank you for rescuing me. Please take my LUTE. It may help you.',
      },
      {
        conditions: [{ flag: StoryFlag.RECEIVED_LUTE, flagSet: true }],
        text: 'I pray for your safety, Warriors of Light.',
      },
    ],
  },

  {
    npcId: 'CASTLE_MINISTER',
    name: 'Minister',
    location: Location.CORNELIA_CASTLE,
    dialogues: [
      {
        conditions: [],
        text: 'The King is beside himself with worry over Princess Sara.',
      },
      {
        conditions: [{ flag: StoryFlag.RESCUED_PRINCESS, flagSet: true }],
        text: 'The Princess has returned! This is a joyous day!',
      },
    ],
  },

  // =========================================================================
  // PRAVOKA - Port Town
  // =========================================================================
  {
    npcId: 'PRAVOKA_SAILOR',
    name: 'Sailor',
    location: Location.PRAVOKA,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.DEFEATED_PIRATES, flagSet: false }],
        text: 'Pirates have taken over the docks! We can\'t leave port!',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_PIRATES, flagSet: true }],
        text: 'You defeated Bikke! Now we can sail again!',
      },
    ],
  },

  {
    npcId: 'PRAVOKA_WOMAN',
    name: 'Woman',
    location: Location.PRAVOKA,
    dialogues: [
      {
        conditions: [],
        text: 'There\'s a witch named Matoya who lives in a cave to the north.',
      },
    ],
  },

  {
    npcId: 'PRAVOKA_MERCHANT',
    name: 'Merchant',
    location: Location.PRAVOKA,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.GOT_SHIP, flagSet: true }],
        text: 'With a ship, you can explore the whole world! Be careful of sea monsters!',
      },
    ],
  },

  // =========================================================================
  // ELFHEIM - Elf Town
  // =========================================================================
  {
    npcId: 'ELFHEIM_ELF_1',
    name: 'Elf',
    location: Location.ELFHEIM,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.ARRIVED_ELFHEIM, flagSet: false }],
        text: 'Welcome to Elfheim, city of the elves.',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_ASTOS, flagSet: false }],
        text: 'A dark elf named Astos has been causing trouble. He may be hiding in the Western Keep.',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_ASTOS, flagSet: true }],
        text: 'You defeated Astos! The elves are in your debt.',
      },
    ],
  },

  {
    npcId: 'ELFHEIM_ELF_2',
    name: 'Elf',
    location: Location.ELFHEIM,
    dialogues: [
      {
        conditions: [],
        text: 'Our White Magic shop has powerful healing spells.',
      },
    ],
  },

  {
    npcId: 'ELFHEIM_PRINCE',
    name: 'Elf Prince',
    location: Location.ELFHEIM,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.FOUND_CROWN, flagSet: false }],
        text: 'The King\'s CROWN was stolen! It may be in the Marsh Cave to the southwest.',
      },
      {
        conditions: [{ flag: StoryFlag.FOUND_CROWN, flagSet: true }],
        text: 'Thank you for recovering the CROWN! Please accept this reward.',
      },
    ],
  },

  // =========================================================================
  // MELMOND - Town of Decay
  // =========================================================================
  {
    npcId: 'MELMOND_OLD_MAN',
    name: 'Old Man',
    location: Location.MELMOND,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.DEFEATED_LICH, flagSet: false }],
        text: 'The earth is rotting. Crops won\'t grow. We\'re all going to starve...',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_LICH, flagSet: true }],
        text: 'The earth is healing! Plants are growing again! You saved us!',
      },
    ],
  },

  {
    npcId: 'MELMOND_SAGE',
    name: 'Sage',
    location: Location.MELMOND,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.DEFEATED_LICH, flagSet: false }],
        text: 'The Earth Crystal has been corrupted by a fiend in the Earth Cave.',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_VAMPIRE, flagSet: false }],
        text: 'A vampire blocks the way to the deepest part of the Earth Cave.',
      },
      {
        conditions: [{ flag: StoryFlag.TITAN_MOVED, flagSet: false }],
        text: 'Beyond the Titan\'s Tunnel lies Sarda, a wise sage who may help you.',
      },
    ],
  },

  {
    npcId: 'MELMOND_WOMAN',
    name: 'Woman',
    location: Location.MELMOND,
    dialogues: [
      {
        conditions: [],
        text: 'My husband went into the Earth Cave and never returned...',
      },
    ],
  },

  // =========================================================================
  // CRESCENT LAKE - Circle of Sages
  // =========================================================================
  {
    npcId: 'ELDER_SAGE',
    name: 'Elder Sage',
    location: Location.CRESCENT_LAKE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.MET_CIRCLE_OF_SAGES, flagSet: false }],
        text: 'Welcome, Warriors of Light. We have been expecting you.',
      },
      {
        conditions: [{ flag: StoryFlag.MET_CIRCLE_OF_SAGES, flagSet: true }],
        text: 'You must defeat all Four Fiends to save the world.',
      },
      {
        conditions: [{ flag: StoryFlag.ALL_CRYSTALS_RESTORED, flagSet: true }],
        text: 'The Crystals shine, but darkness remains. Return to where it all began...',
      },
    ],
  },

  {
    npcId: 'SAGE_2',
    name: 'Second Sage',
    location: Location.CRESCENT_LAKE,
    dialogues: [
      {
        conditions: [],
        text: 'Lich corrupts the Earth, Kary the Fire, Kraken the Water, Tiamat the Wind.',
      },
    ],
  },

  {
    npcId: 'SAGE_3',
    name: 'Third Sage',
    location: Location.CRESCENT_LAKE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.GOT_AIRSHIP, flagSet: false }],
        text: 'Search for the LEVISTONE in the Ice Cave. It will awaken the ancient airship.',
      },
    ],
  },

  {
    npcId: 'SAGE_4',
    name: 'Fourth Sage',
    location: Location.CRESCENT_LAKE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.CLASS_UPGRADE, flagSet: false }],
        text: 'Seek the Dragon King Bahamut. He can unlock your true potential.',
      },
    ],
  },

  // =========================================================================
  // GAIA - Mountain Town
  // =========================================================================
  {
    npcId: 'GAIA_MAN',
    name: 'Man',
    location: Location.GAIA,
    dialogues: [
      {
        conditions: [],
        text: 'The Citadel of Trials lies to the west. Only the strongest can survive it.',
      },
    ],
  },

  {
    npcId: 'GAIA_WOMAN',
    name: 'Woman',
    location: Location.GAIA,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.COMPLETED_CITADEL, flagSet: false }],
        text: 'I heard there\'s a great treasure in the Citadel... but also great danger.',
      },
    ],
  },

  // =========================================================================
  // ONRAC - Beach Resort
  // =========================================================================
  {
    npcId: 'ONRAC_WOMAN',
    name: 'Woman',
    location: Location.ONRAC,
    dialogues: [
      {
        conditions: [],
        text: 'This used to be a peaceful resort town... until the sea began to rage.',
      },
    ],
  },

  {
    npcId: 'ONRAC_FISHERMAN',
    name: 'Fisherman',
    location: Location.ONRAC,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.DEFEATED_KRAKEN, flagSet: false }],
        text: 'Strange creatures have appeared in the sea. Something evil lurks beneath.',
      },
      {
        conditions: [{ flag: StoryFlag.DEFEATED_KRAKEN, flagSet: true }],
        text: 'The sea is calm again! The fish are returning!',
      },
    ],
  },

  // =========================================================================
  // MATOYA'S CAVE
  // =========================================================================
  {
    npcId: 'MATOYA',
    name: 'Matoya',
    location: Location.MATOYA_CAVE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.RETURNED_CRYSTAL_EYE, flagSet: false }],
        text: 'Who\'s there? I can\'t see without my CRYSTAL EYE...',
      },
      {
        conditions: [{ flag: StoryFlag.RETURNED_CRYSTAL_EYE, flagSet: true }],
        text: 'Thank you for returning my eye! If you need anything, just ask.',
      },
    ],
  },

  // =========================================================================
  // SARDA'S CAVE
  // =========================================================================
  {
    npcId: 'SARDA',
    name: 'Sarda',
    location: Location.SARDA_CAVE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.GOT_ROD, flagSet: false }],
        text: 'I am Sarda. I have waited long for you. Take this EARTH ROD.',
      },
      {
        conditions: [{ flag: StoryFlag.GOT_ROD, flagSet: true }],
        text: 'Use the ROD on the stone plate in the Earth Cave.',
      },
    ],
  },

  // =========================================================================
  // BAHAMUT'S CAVE
  // =========================================================================
  {
    npcId: 'BAHAMUT',
    name: 'Bahamut',
    location: Location.BAHAMUT_CAVE,
    dialogues: [
      {
        conditions: [{ flag: StoryFlag.CLASS_UPGRADE, flagSet: false }],
        text: 'I am Bahamut, King of Dragons. Prove your worth in the Citadel of Trials.',
      },
      {
        conditions: [{ flag: StoryFlag.CLASS_UPGRADE, flagSet: true }],
        text: 'You have been reborn, Warriors of Light. Now fulfill your destiny.',
      },
    ],
  },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if story conditions are met
 */
export function checkStoryConditions(
  conditions: StoryCondition[],
  activeFlags: Set<StoryFlag>
): boolean {
  if (conditions.length === 0) return true;

  for (const condition of conditions) {
    if (condition.flag !== undefined) {
      const hasFlag = activeFlags.has(condition.flag);
      const shouldHave = condition.flagSet !== false;

      if (hasFlag !== shouldHave) {
        if (condition.operator === 'OR') continue;
        return false;
      }
    }
  }

  return true;
}

/**
 * Get NPC dialogue based on current story flags
 */
export function getNPCDialogue(
  npcId: string,
  location: Location,
  activeFlags: Set<StoryFlag>
): string {
  const npc = NPC_DIALOGUES.find(n => n.npcId === npcId && n.location === location);
  if (!npc) return "...";

  // Find the first matching dialogue based on conditions
  for (const dialogue of npc.dialogues) {
    if (checkStoryConditions(dialogue.conditions, activeFlags)) {
      return dialogue.text;
    }
  }

  // Fallback
  return npc.dialogues[0]?.text || "...";
}

/**
 * Get all available story events for current location and conditions
 */
export function getAvailableEvents(
  location: Location,
  activeFlags: Set<StoryFlag>
): StoryEvent[] {
  return STORY_EVENTS.filter(event =>
    event.location === location &&
    checkStoryConditions(event.conditions, activeFlags)
  );
}

/**
 * Get story progress percentage
 */
export function getStoryProgress(activeFlags: Set<StoryFlag>): number {
  const majorFlags = [
    StoryFlag.RESCUED_PRINCESS,
    StoryFlag.BRIDGE_BUILT,
    StoryFlag.GOT_SHIP,
    StoryFlag.RESTORED_EARTH_CRYSTAL,
    StoryFlag.GOT_AIRSHIP,
    StoryFlag.CLASS_UPGRADE,
    StoryFlag.RESTORED_FIRE_CRYSTAL,
    StoryFlag.RESTORED_WATER_CRYSTAL,
    StoryFlag.RESTORED_WIND_CRYSTAL,
    StoryFlag.DEFEATED_CHAOS,
  ];

  const completed = majorFlags.filter(flag => activeFlags.has(flag)).length;
  return Math.floor((completed / majorFlags.length) * 100);
}

/**
 * Get current act based on story progress
 */
export function getCurrentAct(activeFlags: Set<StoryFlag>): number {
  if (activeFlags.has(StoryFlag.DEFEATED_CHAOS)) return 5;
  if (activeFlags.has(StoryFlag.ALL_CRYSTALS_RESTORED)) return 5;
  if (activeFlags.has(StoryFlag.CLASS_UPGRADE)) return 4;
  if (activeFlags.has(StoryFlag.GOT_AIRSHIP)) return 3;
  if (activeFlags.has(StoryFlag.RESTORED_EARTH_CRYSTAL)) return 2;
  if (activeFlags.has(StoryFlag.BRIDGE_BUILT)) return 1;
  return 1;
}
